import JoditEditor from 'jodit-react';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {updateBrand, VerdocsEndpoint} from '@verdocs/js-sdk';
import {BUILDER_FONTS, generateCss, seedFromBrand, type IBuilderState} from './BrandBuilderState';
import {ColorControl, InheritControl, SliderControl} from './BrandBuilder';
import {buildDisclosurePreviewHtml, writeIframe} from './BrandPreview';
import {invalidateAllQueries, useBrand} from '../../react-sdk/queries';
import {BuilderFrame, LeaveDialog} from './BuilderFrame';
import {defaultDisclosuresFor} from './BrandDisclosures';
import {track} from '../../Managers/Analytics';
import {Spinner} from '../../Components';
import {AppState} from '../../AppState';

// A trimmed toolbar: the text column is narrower than the old dialog, and disclosures only need
// emphasis, lists, and links.
const EDITOR_CONFIG = {
  readonly: false,
  placeholder: '',
  hidePoweredByJodit: true,
  minHeight: 260,
  toolbarAdaptive: false,
  buttons: ['bold', 'italic', 'underline', '|', 'ul', 'ol', '|', 'link', '|', 'undo', 'redo', '|', 'source'],
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
};

// Jodit re-serializes whatever it is given (attribute order, whitespace), so text is compared
// after a browser round trip rather than byte for byte, or the screen would open already dirty.
const sameHtml = (a: string, b: string) => {
  const normalize = (html: string) => {
    const el = document.createElement('template');
    el.innerHTML = html.trim();
    return el.innerHTML.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
  };
  return normalize(a) === normalize(b);
};

/**
 * Full-screen editor for a brand's disclosure text, with the handful of Theme Editor controls that
 * change the disclosure dialog (color, font, rounding) duplicated beside it. Demos lean on this
 * screen, so every change shows in the preview as it is made.
 */
export const DisclosuresEditor: FC<{brandId: string; onBack: () => void}> = ({brandId, onBack}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const brandQuery = useBrand(organizationId, brandId);
  const brand = brandQuery.data;

  const [published, setPublished] = useState<IBuilderState | null>(null);
  const [draft, setDraft] = useState<IBuilderState | null>(null);
  const [savedText, setSavedText] = useState('');
  const [text, setText] = useState('');
  // Jodit owns its content once mounted, so resets hand it new starting text and a new key.
  const [editorSeed, setEditorSeed] = useState({key: 0, value: ''});
  const [working, setWorking] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const defaultText = useMemo(() => (brand ? defaultDisclosuresFor(brand) : ''), [brand]);

  useEffect(() => {
    if (!brand || published) return;
    const seeded = seedFromBrand(brand);
    const initial = brand.disclaimer?.trim() ? brand.disclaimer : defaultDisclosuresFor(brand);
    setPublished(seeded);
    setDraft(seeded);
    setSavedText(initial);
    setText(initial);
    setEditorSeed({key: 0, value: initial});
  }, [brand, published]);

  const css = useMemo(() => (draft ? generateCss(draft) : ''), [draft]);
  const styleDirty = !!draft && !!published && JSON.stringify(draft) !== JSON.stringify(published);

  const previewHtml = useMemo(
    () =>
      brand && draft
        ? buildDisclosurePreviewHtml({...brand, primary_color: draft.primary, secondary_color: draft.secondary, style_overrides: css, disclaimer: text})
        : '',
    [brand, draft, css, text],
  );
  useEffect(() => {
    if (previewHtml) writeIframe(iframeRef.current, previewHtml);
  }, [previewHtml]);

  if (!brand || !draft || !published) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-white">
        <Spinner mode="dark" />
      </div>
    );
  }

  const textDirty = !sameHtml(text, savedText);
  const dirty = styleDirty || textDirty;
  const isStandardText = sameHtml(text, defaultText);
  const edit = (patch: Partial<IBuilderState>) => setDraft((prev) => (prev ? {...prev, ...patch} : prev));
  const leave = (then: () => void) => (dirty ? setPendingLeave(() => then) : then());

  const loadText = (value: string) => {
    setText(value);
    setEditorSeed((prev) => ({key: prev.key + 1, value}));
  };

  const handleDiscard = () => {
    setDraft(published);
    loadText(savedText);
  };

  const handlePublish = async () => {
    setWorking(true);
    try {
      // Styling goes out only when it changed, so an untouched theme is never regenerated from here.
      await updateBrand(VerdocsEndpoint.getDefault(), organizationId, brand.id, {
        disclaimer: text.trim() && !isStandardText ? text : null,
        ...(styleDirty ? {primary_color: draft.primary, secondary_color: draft.secondary, style_overrides: css} : {}),
      });
      setPublished(draft);
      setSavedText(text);
      invalidateAllQueries();
      track('brand_published', {org_id: organizationId, surface: 'disclosures'});
      toast.success('Published. Signers see the new disclosures from their next session.');
    } catch (e: any) {
      toast.error(`Error publishing: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  return (
    <BuilderFrame
      brand={brand}
      name={brand.name || brand.key}
      logoUrl={brand.thumbnail_url || brand.full_logo_url}
      label="Disclosures"
      dirty={dirty}
      working={working}
      onClose={() => leave(onBack)}
      onDiscard={handleDiscard}
      onPublish={handlePublish}>
      <div className="flex min-h-0 flex-1 flex-col gap-4 laptop:flex-row">
        <div className="flex w-full shrink-0 flex-col overflow-y-auto rounded-xl border border-gray-200 laptop:w-[480px]">
          <div className="border-b border-gray-100 p-3.5">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="text-[13px] font-semibold text-gray-700">Disclosure text</span>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => loadText(defaultText)}
                disabled={isStandardText}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-300">
                Reset to standard text
              </button>
            </div>
            <p className="mb-2.5 text-[11px] leading-relaxed text-gray-500">
              Signers agree to this before they begin. The standard text names your brand and links your legal pages.
            </p>
            <div className="text-sm">
              <JoditEditor key={editorSeed.key} value={editorSeed.value} config={EDITOR_CONFIG} onChange={setText} />
            </div>
          </div>

          <div className="p-3.5">
            <div className="mb-3 flex items-baseline gap-2">
              <span className="text-[13px] font-semibold text-gray-700">Dialog styling</span>
              <span className="text-[11px] text-gray-400">Shared with the Theme Editor.</span>
            </div>
            <ColorControl label="Primary color" value={draft.primary} onChange={(v) => edit({primary: v})} />
            <div className="mb-3.5">
              <label className="mb-1.5 block text-xs font-medium text-gray-600">Font</label>
              <select
                value={draft.font}
                onChange={(e) => edit({font: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-indigo-400 focus:outline-none">
                {!BUILDER_FONTS.some((f) => f.value === draft.font) && <option value={draft.font}>Current: {draft.font}</option>}
                {BUILDER_FONTS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <SliderControl
              label="Dialog corner radius"
              value={draft.dialogRadius}
              min={0}
              max={20}
              display={`${draft.dialogRadius}px`}
              onChange={(v) => edit({dialogRadius: v})}
            />
            <InheritControl
              label="Dialog header accent"
              value={draft.dialogAccent}
              seed={draft.primary}
              note="Inherits your primary color."
              onChange={(v) => edit({dialogAccent: v})}
            />
            <InheritControl
              label="Link color"
              value={draft.linkColor}
              seed={draft.primary}
              note="Inherits your primary color."
              onChange={(v) => edit({linkColor: v})}
            />
            <ColorControl label="Checkbox color" value={draft.checkboxColor} onChange={(v) => edit({checkboxColor: v})} />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 justify-center overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-6">
          <iframe
            ref={iframeRef}
            title="Disclosure preview"
            className="w-full max-w-[620px] shrink-0 rounded-lg border-0 bg-white shadow-sm"
            style={{height: '560px'}}
            sandbox="allow-same-origin"
          />
        </div>
      </div>

      {working && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-[#ffffff7f]">
          <Spinner mode="dark" />
        </div>
      )}

      {pendingLeave && <LeaveDialog onStay={() => setPendingLeave(null)} onLeave={pendingLeave} />}
    </BuilderFrame>
  );
};
